import { FileRevisionSchema } from "@app/types/files";
import { z } from "zod";

// Best-effort event, intentionally outside the request/response RPC protocol so
// older hosts can ignore it without leaving a pending request in Viz.
export const TailwindMissingClassesMessageSchema = z.object({
  type: z.literal("TAILWIND_MISSING_CLASSES"),
  identifier: z.string(),
  buildId: z.string().max(128),
  classNames: z.array(z.string().min(1).max(200)).min(1).max(50),
});

// This defines the commands that the iframe can send to the host window.

// Common base schema.
const VisualizationRPCRequestBaseSchema = z.object({
  identifier: z.string(),
  messageUniqueId: z.string(),
});

// Define parameter schemas for each command.

const GetFileParamsSchema = z.object({
  fileId: z.string(),
});

const WriteFileParamsSchema = z.object({
  path: z.string(),
  content: z.string(),
  contentType: z.string().optional(),
  revision: FileRevisionSchema,
});

export type WriteFileParams = z.infer<typeof WriteFileParamsSchema>;

export type WriteFileResult =
  | { success: true; revision: string }
  | {
      success: false;
      error: {
        code: "read_only" | "invalid_path" | "conflict" | "save_failed";
        message: string;
      };
    };

const CallFunctionParamsSchema = z.object({
  functionIdOrSlug: z.string(),
  input: z.unknown().optional(),
});

export interface WorkspaceUserIdentity {
  sId: string;
  firstName: string;
  lastName: string | null;
  fullName: string;
  image: string | null;
}

export type UserIdentityState =
  | {
      isAuthenticated: true;
      isWorkspaceMember: true;
      // Whether the viewer is an editor of the Pod hosting the Frame. Display-only: functions
      // resolve the caller server-side, never from what the Frame sends.
      isPodEditor: boolean;
      // Whether the viewer belongs to one of the Pod's groups (member or editor). Display-only,
      // like isPodEditor: gate what the Frame shows, never what the server allows.
      isPodMember: boolean;
      // Whether the viewer can modify the source files backing this Frame v2. Display-only: use
      // frame_author_required to enforce the same capability on a server-side function.
      canWriteFrameSource: boolean;
      user: WorkspaceUserIdentity;
    }
  | {
      isAuthenticated: false;
      isWorkspaceMember: false;
      isPodEditor: false;
      isPodMember: false;
      canWriteFrameSource: false;
      user: null;
    };

export interface ScopedWorkspaceUserIdentity {
  workspaceId: string;
  // Optional: only hosts rendering inside a pod know editorship and membership; absent means
  // false.
  isPodEditor?: boolean;
  isPodMember?: boolean;
  user: WorkspaceUserIdentity;
}

const SetContentHeightParamsSchema = z.object({
  height: z.number(),
});

const DownloadFileRequestParamsSchema = z.object({
  blob: z.instanceof(Blob),
  filename: z.string().optional(),
});

const SetErrorMessageParamsSchema = z.object({
  errorMessage: z.string(),
  fileId: z.string(),
  isInteractiveContent: z.boolean(),
});

// Define Zod schemas for each RPC request type.
const GetFileRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("getFile"),
  params: GetFileParamsSchema,
});

export const WriteFileRequestEnvelopeSchema =
  VisualizationRPCRequestBaseSchema.extend({
    command: z.literal("writeFile"),
  });

const WriteFileRequestSchema = WriteFileRequestEnvelopeSchema.extend({
  params: WriteFileParamsSchema,
});

const CallFunctionRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("callFunction"),
  params: CallFunctionParamsSchema,
});

const GetUserIdentityRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("getUserIdentity"),
  params: z.null(),
});

const GetCodeToExecuteRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("getCodeToExecute"),
  params: z.null(),
});

const SetContentHeightRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("setContentHeight"),
  params: SetContentHeightParamsSchema,
});

const SetErrorMessageRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("setErrorMessage"),
  params: SetErrorMessageParamsSchema,
});

const DownloadFileRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("downloadFileRequest"),
  params: DownloadFileRequestParamsSchema,
});

const DisplayCodeRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("displayCode"),
  params: z.null(),
});

const EditTextParamsSchema = z.object({
  newText: z.string(),
  oldText: z.string(),
  targetFileId: z.string().optional(),
  // When set, the edit is routed back to the source by location: the value is the clicked
  // element's `data-source` ("<relPath>:<line>:<col>") from a published (bundled) Frame, and
  // oldText/newText are the visible (trimmed) text. Absent for legacy context-match edits.
  source: z.string().optional(),
});

type EditTextParams = z.infer<typeof EditTextParamsSchema>;

export type EditTextFn = (
  params: EditTextParams
) => Promise<{ success: boolean; error?: string }>;

const EditTextRequestSchema = VisualizationRPCRequestBaseSchema.extend({
  command: z.literal("editText"),
  params: EditTextParamsSchema,
});

const VisualizationRPCRequestSchema = z.union([
  CallFunctionRequestSchema,
  GetUserIdentityRequestSchema,
  GetFileRequestSchema,
  WriteFileRequestSchema,
  GetCodeToExecuteRequestSchema,
  SetContentHeightRequestSchema,
  SetErrorMessageRequestSchema,
  DownloadFileRequestSchema,
  DisplayCodeRequestSchema,
  EditTextRequestSchema,
]);

// Derive types from Zod schemas.
export type VisualizationRPCRequest = z.infer<
  typeof VisualizationRPCRequestSchema
>;
export type CallFunctionRequest = z.infer<typeof CallFunctionRequestSchema>;
export type VisualizationRPCCommand = VisualizationRPCRequest["command"];

// Command results.
export interface CommandResultMap {
  callFunction: unknown;
  getUserIdentity: UserIdentityState;
  getCodeToExecute: { code: string };
  getFile: {
    fileBlob: Blob | null;
    revision?: string | null;
    canWrite?: boolean;
  };
  writeFile: WriteFileResult;
  downloadFileRequest: { blob: Blob; filename?: string };
  setContentHeight: void;
  setErrorMessage: void;
  displayCode: void;
  editText: { success: boolean; error?: string };
}

export function isVisualizationRPCRequest(
  value: unknown
): value is VisualizationRPCRequest {
  return VisualizationRPCRequestSchema.safeParse(value).success;
}
